import React, { useState, useRef, useEffect } from "react";
import { Box, Typography, TextField, IconButton, Stack, CircularProgress, Chip } from "@mui/material";
import { Send, MessageCircle, Bot, User, Sparkles } from "lucide-react";
import { toast } from "react-toastify";
import Markdown from "react-markdown";
import { baseChatApi } from "../../services/api/legalChatApi";

const AskChatbot = ({ chatContext, component }) => {
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const messageContainerRef = useRef(null);

  // API Call for chatbot response
  const generateResponse = async (userMessage) => {
    try {
      return await baseChatApi(userMessage, chatContext);
    } catch (error) {
      console.error("Error fetching chatbot response:", error);
      throw new Error("Failed to generate chatbot response");
    }
  };

  // Function to handle sending messages
  const handleSendMessage = async () => {
    if (userInput.trim() === "") return;

    const userMessage = {
      sender: "User",
      text: userInput,
    };

    setMessages((prevState) => [...prevState, userMessage]);
    setUserInput("");
    setIsLoading(true);

    try {
      const chatbotResponse = await generateResponse(userInput);

      const botResponse = {
        sender: "Bot",
        text: chatbotResponse.response,
      };

      setMessages((prevMessages) => [...prevMessages, botResponse]);
      setIsLoading(false);
    } catch (error) {
      console.error("Error generating chatbot response:", error);
      toast.error("Failed to generate chatbot response. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEnter = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  useEffect(() => {
    if (messageContainerRef.current) {
      messageContainerRef.current.scrollTop =
        messageContainerRef.current.scrollHeight;
    }
  }, [messages]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: { xs: "100%", lg: "320px" },
        height: "100%",
        backgroundColor: "#FFFFFF",
        borderLeft: { xs: "none", lg: "1px solid #F1F5F9" },
        boxShadow: { xs: "0 -4px 12px rgba(0, 0, 0, 0.1)", lg: "none" },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          padding: "20px 24px",
          borderBottom: "1px solid #F1F5F9",
          backgroundColor: "#FFFFFF",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "36px",
            height: "36px",
            borderRadius: "10px",
            backgroundColor: "#F0F9FF",
            color: "#00509e",
          }}
        >
          <Sparkles size={20} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography
            sx={{
              fontSize: "16px",
              fontWeight: "600",
              color: "#1E293B",
              fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
              marginBottom: "2px",
            }}
          >
            {component === "case" ? "Case Insights" : "Statute Insights"}
          </Typography>
          <Typography
            sx={{
              fontSize: "13px",
              color: "#64748B",
              fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
            }}
          >
            Ask AI-powered questions
          </Typography>
        </Box>
      </Box>

      {/* Messages Container */}
      <Box
        ref={messageContainerRef}
        sx={{
          flex: 1,
          overflowY: "auto",
          padding: "20px 24px",
          backgroundColor: "#FAFBFC",
          "::-webkit-scrollbar": {
            width: "6px",
          },
          "::-webkit-scrollbar-track": {
            background: "#F8FAFC",
          },
          "::-webkit-scrollbar-thumb": {
            background: "#CBD5E1",
            borderRadius: "8px",
            "&:hover": {
              background: "#94A3B8",
            },
          },
        }}
      >
        {messages.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              textAlign: "center",
              padding: "40px 20px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "64px",
                height: "64px",
                borderRadius: "16px",
                backgroundColor: "#F0F9FF",
                color: "#00509e",
                marginBottom: "20px",
              }}
            >
              <MessageCircle size={32} />
            </Box>
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: "600",
                color: "#1E293B",
                fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                marginBottom: "8px",
              }}
            >
              Start a conversation
            </Typography>
            <Typography
              sx={{
                fontSize: "14px",
                color: "#64748B",
                fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                lineHeight: "1.5",
                maxWidth: "240px",
              }}
            >
              {component === "case"
                ? "Ask questions about this case to get AI-powered insights and analysis."
                : "Ask questions about this statute to get AI-powered insights and explanations."}
            </Typography>
          </Box>
        ) : (
          <Stack spacing={3}>
            {messages.map((message, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  justifyContent: message.sender === "User" ? "flex-end" : "flex-start",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 2,
                    maxWidth: "85%",
                  }}
                >
                  {message.sender === "Bot" && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "28px",
                        height: "28px",
                        borderRadius: "8px",
                        backgroundColor: "#F0F9FF",
                        color: "#00509e",
                        flexShrink: 0,
                        marginTop: "4px",
                      }}
                    >
                      <Bot size={16} />
                    </Box>
                  )}
                  <Box
                    sx={{
                      backgroundColor: message.sender === "User" ? "#00509e" : "#FFFFFF",
                      color: message.sender === "User" ? "#FFFFFF" : "#1E293B",
                      padding: "12px 16px",
                      borderRadius: message.sender === "User" ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                      border: message.sender === "Bot" ? "1px solid #E2E8F0" : "none",
                      boxShadow: message.sender === "Bot" ? "0 1px 3px rgba(0, 0, 0, 0.05)" : "none",
                      position: "relative",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "14px",
                        lineHeight: "1.6",
                        fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                        "& p": {
                          margin: 0,
                          marginBottom: "8px",
                          "&:last-child": {
                            marginBottom: 0,
                          },
                        },
                        "& ul, & ol": {
                          margin: "8px 0",
                          paddingLeft: "20px",
                        },
                        "& li": {
                          marginBottom: "4px",
                        },
                        "& code": {
                          backgroundColor: message.sender === "User" ? "rgba(255, 255, 255, 0.2)" : "#F1F5F9",
                          padding: "2px 6px",
                          borderRadius: "4px",
                          fontSize: "13px",
                          fontFamily: "'Monaco', 'Menlo', 'Ubuntu Mono', monospace",
                        },
                        "& pre": {
                          backgroundColor: message.sender === "User" ? "rgba(255, 255, 255, 0.1)" : "#F8FAFC",
                          padding: "12px",
                          borderRadius: "8px",
                          overflow: "auto",
                          margin: "8px 0",
                          "& code": {
                            backgroundColor: "transparent",
                            padding: 0,
                          },
                        },
                      }}
                    >
                      <Markdown
                        components={{
                          p: ({ children }) => (
                            <Typography variant="body2" sx={{ mb: 0 }}>
                              {children}
                            </Typography>
                          ),
                        }}
                      >
                        {message.text}
                      </Markdown>
                    </Typography>
                  </Box>
                  {message.sender === "User" && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "28px",
                        height: "28px",
                        borderRadius: "8px",
                        backgroundColor: "#00509e",
                        color: "#FFFFFF",
                        flexShrink: 0,
                        marginTop: "4px",
                      }}
                    >
                      <User size={16} />
                    </Box>
                  )}
                </Box>
              </Box>
            ))}
            
            {/* Loading Indicator */}
            {isLoading && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 2,
                    maxWidth: "85%",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "28px",
                      height: "28px",
                      borderRadius: "8px",
                      backgroundColor: "#F0F9FF",
                      color: "#00509e",
                      flexShrink: 0,
                      marginTop: "4px",
                    }}
                  >
                    <Bot size={16} />
                  </Box>
                  <Box
                    sx={{
                      backgroundColor: "#FFFFFF",
                      padding: "16px 20px",
                      borderRadius: "16px 16px 16px 4px",
                      border: "1px solid #E2E8F0",
                      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    <CircularProgress size={16} sx={{ color: "#00509e" }} />
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: "#64748B",
                        fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                      }}
                    >
                      Thinking...
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
          </Stack>
        )}
      </Box>

      {/* Input Area */}
      <Box
        sx={{
          padding: "20px 24px",
          borderTop: "1px solid #F1F5F9",
          backgroundColor: "#FFFFFF",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            gap: 2,
            backgroundColor: "#F8FAFC",
            borderRadius: "16px",
            padding: "12px 16px",
            border: "1px solid #E2E8F0",
            transition: "all 0.2s ease-in-out",
            "&:focus-within": {
              borderColor: "#00509e",
              boxShadow: "0 0 0 3px rgba(0, 80, 158, 0.1)",
            },
          }}
        >
          <TextField
            multiline
            maxRows={4}
            placeholder="Type your question..."
            value={userInput}
            onChange={(e) => setUserInput(e.target.value)}
            onKeyDown={handleEnter}
            variant="standard"
            sx={{
              flex: 1,
              "& .MuiInputBase-root": {
                fontSize: "14px",
                fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                color: "#1E293B",
                "&::before": {
                  borderBottom: "none",
                },
                "&::after": {
                  borderBottom: "none",
                },
                "&:hover::before": {
                  borderBottom: "none",
                },
                "&.Mui-focused::after": {
                  borderBottom: "none",
                },
              },
              "& .MuiInputBase-input": {
                "&::placeholder": {
                  color: "#94A3B8",
                  opacity: 1,
                },
              },
            }}
          />
          <IconButton
            onClick={handleSendMessage}
            disabled={!userInput.trim() || isLoading}
            sx={{
              width: "36px",
              height: "36px",
              borderRadius: "10px",
              backgroundColor: userInput.trim() && !isLoading ? "#00509e" : "#F1F5F9",
              color: userInput.trim() && !isLoading ? "#FFFFFF" : "#CBD5E1",
              "&:hover": {
                backgroundColor: userInput.trim() && !isLoading ? "#003366" : "#E2E8F0",
              },
              "&:disabled": {
                backgroundColor: "#F1F5F9",
                color: "#CBD5E1",
              },
              transition: "all 0.2s ease-in-out",
            }}
          >
            <Send size={18} />
          </IconButton>
        </Box>
        <Typography
          sx={{
            fontSize: "12px",
            color: "#94A3B8",
            fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
            marginTop: "8px",
            textAlign: "center",
          }}
        >
          Press Enter to send, Shift+Enter for new line
        </Typography>
      </Box>
    </Box>
  );
};

export default AskChatbot;
